// tslint:disable:triple-equals

import { updateValidation, UPDATE_VALIDATION, IMeta } from "../reducers/metaReducer";
import i18n from "../i18n/i18n";
import * as moment from "moment";
import IInvoiceVm from "../models/home/Payment/IInvoiceVm";
import { CertificateProcessType } from "../models/flags/CertificateProcessType";
import { FormerLicenseType } from "../models/enums/FormerLicenseType";
import { MedicalCertificateStatusType } from "../models/enums/MedicalCertificateStatusType";
import IUserVm from "../models/shared/IUserVm";
import { UserType } from "../models/flags/UserType";
import ICreateApplicationForExemptedCitizenVm from "../models/issuingAuthority/ICreateApplicationForExemptedCitizenVm";
import { ActorSubmittingApplicationType } from "../models/enums/ActorSubmittingApplicationType";
import ICreateApplicationForReorderingOfCertificateVm from "../models/issuingAuthority/ICreateApplicationForReorderingOfCertificateVm";
import ITeamVm from "../models/shared/ITeamVm";
import IApplicationForTaxVm from "../models/shared/IApplicationForTaxVm";
import IApplicationVm from "../models/shared/IApplicationVm";
import { CodeOfConductStatusType } from "../models/enums/CodeOfConductStatusType";
import { CriminalAssessmentStatusType } from "../models/enums/CriminalAssessmentStatusType";
import InitState from "./initState";
import { CertificateType } from "../models/flags/CertificateType";
import * as ValidationConstants from "./validationConstants";
import IApplicationForCubVm from "../models/shared/IApplicationForCubVm";
import { SelectedPayorType } from "../pages/payment/Payment";
import { PaymentType } from "../models/enums/PaymentType";
import IApplicationForVubVm from "../models/shared/IApplicationForVubVm";
import { ExaminationResultType } from "../models/enums/ExaminationResultType";
import IErrorVm from "../models/home/Error/IErrorVm";
import UserHelper from "../services/userHelper";
import IApplicationForStaVm from "../models/shared/IApplicationForStaVm";
import { EducationalCourseType } from "../models/enums/EducationalCourseType";
import { CriminalRecordDeclarationStatus } from "../models/enums/CriminalRecordDeclarationStatus";
import IApplicationForAdrVm from "../models/shared/IApplicationForAdrVm";

// tslint:disable-next-line:one-variable-per-declaration
type MiddlewareFunction = (store: any) => (next: any) => (action: any) => any;
let errors: IErrorVm[];

export const ValidateStore: MiddlewareFunction = (store) => (next) => (action) => {
  errors = [];
  // run before redux
  const returnValue = next(action);
  if (action.type !== UPDATE_VALIDATION) {
    const state: InitState = store.getState();

    // Get all applications from state
    // If any applications exist and there is a selectedApplicationId, go through validations with that selected application
    const applications = state.applications as any[];
    if (applications && (state.meta as IMeta).selectedApplicationId) {
      for (const application of applications.filter((x) => x.id === (state.meta as IMeta).selectedApplicationId)) {
        applicationValidator(application);
      }
    }

    if (state.invoice as IInvoiceVm) {
      invoiceValidator((state as InitState).invoice, (state as InitState).meta.selectedPayor);
    }

    if (state.applicationForExempted) {
      applicationForExemptedValidator((state as InitState).applicationForExempted);
    }

    if (state.applicationForReorderingOfCertificate) {
      applicationForReorderingOfCertificateValidator((state as InitState).applicationForReorderingOfCertificate);
    }

    if (state.meta.selectedTeam) {
      teamValidator((state.meta as IMeta).selectedTeam);
    }

    // After all validations is run, update errors in store
    store.dispatch(updateValidation(errors));
  }
  return returnValue;
};

// ApplicationValidator validates fields on IApplicationVm, and then validates for either TAX, CUB, VUB etc. depending on the certificateType
const applicationValidator = (application: IApplicationVm) => {
  required(application.citizen.civilRegistrationIdentifier, ValidationConstants.APPLICATION_CITIZEN_CIVILREGISTRATIONIDENTIFIER, application.id);
  required(application.citizen.firstName, ValidationConstants.APPLICATION_CITIZEN_FIRSTNAME, application.id);
  required(application.driverLicense.base64FrontImage, ValidationConstants.APPLICATION_DRIVERLICENSE_BASE64FRONTIMAGE, application.id);
  required(application.driverLicense.base64BackImage, ValidationConstants.APPLICATION_DRIVERLICENSE_BASE64BACKIMAGE, application.id);

  if (application.examinationResultType !== ExaminationResultType.Failed) {
    dateGreaterThatToday(application.driverLicense.issueDate, ValidationConstants.APPLICATION_DRIVERLICENSE_ISSUEDATE, application.id);
  }

  requiredIf(application.citizen.useSecondDeliveryAddress, application.citizen.secondDeliveryAddress.combinedStreetAddressLine, ValidationConstants.APPLICATION_CITIZEN_SECONDDELIVERYADDRESS_COMBINEDSTREETADDRESSLINE, application.id);
  requiredIf(application.citizen.useSecondDeliveryAddress, application.citizen.secondDeliveryAddress.postalCode, ValidationConstants.APPLICATION_CITIZEN_SECONDDELIVERYADDRESS_POSTALCODE, application.id);
  requiredIf(application.citizen.useSecondDeliveryAddress, application.citizen.secondDeliveryAddress.postalDistrict, ValidationConstants.APPLICATION_CITIZEN_SECONDDELIVERYADDRESS_POSTALDISTRICT, application.id);
  requiredIf(application.citizen.useSecondDeliveryAddress, application.citizen.secondDeliveryAddress.countryName, ValidationConstants.APPLICATION_CITIZEN_SECONDDELIVERYADDRESS_COUNTRYNAME, application.id);

  // TODO: Refactor this where it is used
  const user: IUserVm = UserHelper.currentUser();
  if (user.type == UserType.IssuingAuthorityCaseworker || user.type == UserType.IssuingAuthorityAdministrativeCaseWorker || user.type == UserType.EducationalWorker) {
    const address = (application as IApplicationVm).citizen.address;
    const secondDeliveryAddress = (application as IApplicationVm).citizen.secondDeliveryAddress;
    const addressIsOk = !(application as IApplicationVm).citizen.useSecondDeliveryAddress && !!address.combinedStreetAddressLine && !!address.postalCode && !!address.postalDistrict;
    const secondDeliveryAddressIsOk = !!(application as IApplicationVm).citizen.useSecondDeliveryAddress && !!secondDeliveryAddress.combinedStreetAddressLine && !!secondDeliveryAddress.postalCode && !!secondDeliveryAddress.postalDistrict;
    required(addressIsOk || secondDeliveryAddressIsOk, "AddressError", application.id);
  }

  switch (application.certificateType) {
    case CertificateType.Tax:
      return applicationForTaxValidator(application as IApplicationForTaxVm);
    case CertificateType.Cub:
      return applicationForCubValidator(application as IApplicationForCubVm);
    case CertificateType.Sta:
      return applicationForStaValidator(application as IApplicationForStaVm);
    case CertificateType.Vub:
      return applicationForVubValidator(application as IApplicationForVubVm);
    case CertificateType.Adr:
      return applicationForAdrValidator(application as IApplicationForAdrVm);
  }
};

const applicationForTaxValidator = (application: IApplicationForTaxVm) => {
  required(application.isStatementOfConsentMedicalCertificateAccepted, ValidationConstants.APPLICATION_ISSTATEMENTOFCONSENTMEDICALCERTIFICATEACCEPTED, application.id);
  required(application.isStatementOfConsentCriminalRecordAccepted, ValidationConstants.APPLICATION_ISSTATEMENTOFCONSENTCRIMINALRECORDACCEPTED, application.id);
  required(application.isNameConfirmed, ValidationConstants.APPLICATION_ISNAMECONFIRMED, application.id);
  required(application.medicalCertificateMarkedAsSent, ValidationConstants.APPLICATION_MEDICALCERTIFICATEMARKEDASSENT, application.id);

  requiredIf(application.examinationResultType !== ExaminationResultType.Failed, application.driverLicense.number, ValidationConstants.APPLICATION_DRIVERLICENSE_NUMBER, application.id);
  requiredIf(application.examinationResultType !== ExaminationResultType.Failed, application.base64ProfileImage, ValidationConstants.APPLICATION_BASE64PROFILEIMAGE, application.id);

  requiredIf(Number(application.medicalCertificateStatus) === MedicalCertificateStatusType.AcceptedWithConditions, application.certificateExpireTimeSpanId, ValidationConstants.APPLICATION_CERTIFICATEEXPIRETIMESPANID, application.id, true);

  // Validation for that caseworker cant approve application if medicalcertificate, codeOfConduct or criminalAssessment is rejected.
  // For medicalCertificateStatus value must be Accepted or AcceptedWithConditions.
  // Only required if certificateProcessType is Issuing, Renewal or RenewalBasedOnExternal
  requiredIf(application.certificateProcessType == CertificateProcessType.Issuing || application.certificateProcessType == CertificateProcessType.Renewal || application.certificateProcessType == CertificateProcessType.RenewalBasedOnExternal, Number(application.medicalCertificateStatus) === MedicalCertificateStatusType.Accepted || Number(application.medicalCertificateStatus) === MedicalCertificateStatusType.AcceptedWithConditions, "canApproveTaxApplication", application.id);

  // For codeofConductStatus value must be 1 for caseworker to approve
  requiredIf(application.certificateProcessType == CertificateProcessType.Issuing || application.certificateProcessType == CertificateProcessType.Renewal || application.certificateProcessType == CertificateProcessType.RenewalBasedOnExternal, Number(application.codeOfConductStatus) === CodeOfConductStatusType.Approved, "canApproveTaxApplication", application.id);

  // For criminalAssessmentStatus value must be 1 for caseworker to approve
  requiredIf(application.certificateProcessType == CertificateProcessType.Issuing || application.certificateProcessType == CertificateProcessType.Renewal || application.certificateProcessType == CertificateProcessType.RenewalBasedOnExternal, Number(application.criminalAssessmentStatus) === CriminalAssessmentStatusType.Approved, "canApproveTaxApplication", application.id);

  requiredIfEnum(application.certificateProcessType === CertificateProcessType.Issuing || application.certificateProcessType === CertificateProcessType.Renewal || application.certificateProcessType === CertificateProcessType.RenewalBasedOnExternal, application.medicalCertificateStatus, ValidationConstants.APPLICATION_MEDICALCERTIFICATESTATUS, application.id);

  requiredIfEnum(application.certificateProcessType === CertificateProcessType.Issuing || application.certificateProcessType === CertificateProcessType.Renewal || application.certificateProcessType === CertificateProcessType.RenewalBasedOnExternal, application.codeOfConductStatus, ValidationConstants.APPLICATION_CODEOFCONDUCTSTATUS, application.id);

  requiredIfEnum(application.certificateProcessType === CertificateProcessType.Issuing || application.certificateProcessType === CertificateProcessType.Renewal || application.certificateProcessType === CertificateProcessType.RenewalBasedOnExternal, application.criminalAssessmentStatus, ValidationConstants.APPLICATION_CRIMINALASSESSMENTSTATUS, application.id);

  requiredEnum(application.examinationResultType, ValidationConstants.APPLICATION_EXAMINATIONRESULTTYPE, application.id);

  // Validation for previousApplicationForTaxBasedOnExternal
  if (application.previousApplicationForTaxBasedOnExternal) {
    const previousApplicationForTaxBasedOnExternal = application.previousApplicationForTaxBasedOnExternal;

    requiredEnum(previousApplicationForTaxBasedOnExternal.licenseType, ValidationConstants.APPLICATION_PREVIOUSAPPLICATIONFORTAXBASEDONEXTERNAL_LICENSETYPE, application.id);

    if (previousApplicationForTaxBasedOnExternal.licenseType === FormerLicenseType.TaxLicense) {
      required(previousApplicationForTaxBasedOnExternal.issuingMunicipality !== null ? previousApplicationForTaxBasedOnExternal.issuingMunicipality.municipalityName : "", ValidationConstants.APPLICATION_PREVIOUSAPPLICATIONFORTAXBASEDONEXTERNAL_ISSUINGMUNICIPALITY, application.id);
      required(previousApplicationForTaxBasedOnExternal.taxLicenseNumber, ValidationConstants.APPLICATION_PREVIOUSAPPLICATIONFORTAXBASEDONEXTERNAL_TAXLICENSENUMBER, application.id);
      required(previousApplicationForTaxBasedOnExternal.base64TaxLicenseBackImage, ValidationConstants.APPLICATION_PREVIOUSAPPLICATIONFORTAXBASEDONEXTERNAL_BASE64TAXLICENSEBACKIMAGE, application.id);
      required(previousApplicationForTaxBasedOnExternal.base64TaxLicenseFrontImage, ValidationConstants.APPLICATION_PREVIOUSAPPLICATIONFORTAXBASEDONEXTERNAL_BASE64TAXLICENSEFRONTIMAGE, application.id);
      requiredIfDate(application.certificateProcessType === CertificateProcessType.RenewalBasedOnExternal || application.certificateProcessType === CertificateProcessType.ChangeBasedOnExternal || application.certificateProcessType === CertificateProcessType.ReplacementBasedOnExternal, previousApplicationForTaxBasedOnExternal.expireDate, ValidationConstants.APPLICATION_PREVIOUSAPPLICATIONFORTAXBASEDONEXTERNAL_EXPIREDATE, application.id);
    } else if (previousApplicationForTaxBasedOnExternal.licenseType === FormerLicenseType.EducationalLicense) {
      requiredIf(previousApplicationForTaxBasedOnExternal.licenseType === FormerLicenseType.EducationalLicense, previousApplicationForTaxBasedOnExternal.base64EducationalLicenseImage, ValidationConstants.APPLICATION_PREVIOUSAPPLICATIONFORTAXBASEDONEXTERNAL_BASE64EDUCATIONALLICENSEIMAGE, application.id);
    }
  }

  dateGreaterThatToday(application.examinationResultDate, ValidationConstants.APPLICATION_EXAMINATIONRESULTDATE, application.id);

  requiredIfEnum(
    application.examinationResultType !== ExaminationResultType.Failed, // Is not reqired if examinationResultType is Failed
    application.selectedPaymentType,
    ValidationConstants.APPLICATION_SELECTEDPAYMENTTYPE,
    application.id,
    "Betalingsform skal vælges"
  );

  requiredIf(
    application.examinationResultType !== ExaminationResultType.Failed && // Is not reqired if examinationResultType is Failed
      (application.selectedPaymentType === PaymentType.OnlinePaymentForCompany || application.selectedPaymentType === PaymentType.ElectronicInvoice || application.selectedPaymentType === PaymentType.OnlineBulkPaymentForCompany),
    !!application.companyChosenForPayment && (!!application.companyChosenForPayment.companyRegistrationIdentifier || !!application.companyChosenForPayment.electronicInvoiceNumber),
    ValidationConstants.APPLICATION_COMPANYCHOSENFORPAYMENT,
    application.id,
    null,
    "Betaler skal vælges"
  );

  requiredIf(application.selectedPaymentType === PaymentType.ElectronicInvoice, !!application.companyChosenForPayment && application.companyChosenForPayment.reference, ValidationConstants.APPLICATION_COMPANYCHOSENFORPAYMENT_REFERENCE, application.id);
};

const applicationForCubValidator = (application: IApplicationForCubVm) => {
  // If examinationResultType is sat to Failed, the educational worker should still be able to
  // submit the application, without filling out all the information that normally is required for a VUB application.
  requiredIf(
    application.examinationResultType !== ExaminationResultType.Failed, // Is not reqired if examinationResultType is Failed
    application.driverLicense.number,
    ValidationConstants.APPLICATION_DRIVERLICENSE_NUMBER,
    application.id
  );

  requiredIf(
    application.examinationResultType !== ExaminationResultType.Failed, // Is not reqired if examinationResultType is Failed
    application.base64ProfileImage,
    ValidationConstants.APPLICATION_BASE64PROFILEIMAGE,
    application.id
  );

  requiredIf(
    application.examinationResultType !== ExaminationResultType.Failed, // Is not reqired if examinationResultType is Failed
    application.base64SignatureImage,
    ValidationConstants.APPLICATION_BASE64SIGNATUREIMAGE,
    application.id,
    null,
    "Underskrift mangler"
  );

  requiredIfCharLength(!application.driverLicense.isLicenseForeign, application.driverLicense.number, 8, ValidationConstants.APPLICATION_DRIVERLICENSE_NUMBER, application.id, "Kørekortnummer skal indeholde minimum 8 tegn");

  requiredIfEnum(application.examinationResultType !== ExaminationResultType.Failed, application.driverLicense.chosenLicenseCategories, ValidationConstants.APPLICATION_DRIVERLICENSE_CHOSENLICENSECATEGORIES, application.id, "Kategorier skal vælges");

  requiredEnum(application.chosenEducationalCourseType, ValidationConstants.APPLICATION_CHOSENEDUCATIONALCOURSETYPE, application.id, "Uddannelse skal vælges");

  requiredEnum(application.examinationResultType, ValidationConstants.APPLICATION_EXAMINATIONRESULTTYPE, application.id);

  dateGreaterThatToday(application.examinationResultDate, ValidationConstants.APPLICATION_EXAMINATIONRESULTDATE, application.id);

  requiredIfEnum(
    application.examinationResultType !== ExaminationResultType.Failed, // Is not reqired if examinationResultType is Failed
    application.selectedPaymentType,
    ValidationConstants.APPLICATION_SELECTEDPAYMENTTYPE,
    application.id,
    "Betalingsform skal vælges"
  );

  requiredIf(
    application.examinationResultType !== ExaminationResultType.Failed && // Is not reqired if examinationResultType is Failed
      (application.selectedPaymentType === PaymentType.OnlinePaymentForCompany || application.selectedPaymentType === PaymentType.ElectronicInvoice || application.selectedPaymentType === PaymentType.OnlineBulkPaymentForCompany),
    !!application.companyChosenForPayment && (!!application.companyChosenForPayment.companyRegistrationIdentifier || !!application.companyChosenForPayment.electronicInvoiceNumber),
    ValidationConstants.APPLICATION_COMPANYCHOSENFORPAYMENT,
    application.id,
    null,
    "Betaler skal vælges"
  );

  requiredIf(
    application.examinationResultType !== ExaminationResultType.Failed, // Is not reqired if examinationResultType is Failed
    !!application.citizen.birthPlaceCountry ? application.citizen.birthPlaceCountry.id : "",
    ValidationConstants.APPLICATION_CITIZEN_BIRTHPLACECOUNTRY,
    application.id
  );

  requiredIf(application.selectedPaymentType === PaymentType.ElectronicInvoice, !!application.companyChosenForPayment && application.companyChosenForPayment.reference, ValidationConstants.APPLICATION_COMPANYCHOSENFORPAYMENT_REFERENCE, application.id);

  requiredIfDate(application.certificateProcessType === CertificateProcessType.Issuing && application.chosenEducationalCourseType === EducationalCourseType.ContinuingEducation && application.isContinuingEducationBasedOnNonBatApplication === false, application.previousCertificateExpireDate, ValidationConstants.APPLICATION_PREVIOUSCERTIFICATEEXPIREDATE, application.id);
};

const applicationForStaValidator = (application: IApplicationForStaVm) => {
  // If examinationResultType is sat to Failed, the educational worker should still be able to
  // submit the application, without filling out all the information that normally is required for a VUB application.
  requiredIf(
    application.examinationResultType !== ExaminationResultType.Failed, // Is not reqired if examinationResultType is Failed
    application.driverLicense.number,
    ValidationConstants.APPLICATION_DRIVERLICENSE_NUMBER,
    application.id
  );

  requiredIf(
    application.examinationResultType !== ExaminationResultType.Failed, // Is not reqired if examinationResultType is Failed
    application.base64ProfileImage,
    ValidationConstants.APPLICATION_BASE64PROFILEIMAGE,
    application.id
  );

  requiredIf(
    application.examinationResultType !== ExaminationResultType.Failed, // Is not reqired if examinationResultType is Failed
    application.base64SignatureImage,
    ValidationConstants.APPLICATION_BASE64SIGNATUREIMAGE,
    application.id,
    null,
    "Underskrift mangler"
  );

  requiredIfCharLength(!application.driverLicense.isLicenseForeign, application.driverLicense.number, 8, ValidationConstants.APPLICATION_DRIVERLICENSE_NUMBER, application.id, "Kørekortnummer skal indeholde minimum 8 tegn");

  // requiredEnum(application.chosenEducationalCourseType,
  //     ValidationConstants.APPLICATION_CHOSENEDUCATIONALCOURSETYPE,
  //     application.id,
  //     "Uddannelse skal vælges")

  requiredEnum(application.examinationResultType, ValidationConstants.APPLICATION_EXAMINATIONRESULTTYPE, application.id);

  dateGreaterThatToday(application.examinationResultDate, ValidationConstants.APPLICATION_EXAMINATIONRESULTDATE, application.id);

  requiredIfEnum(
    application.examinationResultType !== ExaminationResultType.Failed, // Is not reqired if examinationResultType is Failed
    application.selectedPaymentType,
    ValidationConstants.APPLICATION_SELECTEDPAYMENTTYPE,
    application.id,
    "Betalingsform skal vælges"
  );

  requiredIf(
    application.examinationResultType !== ExaminationResultType.Failed && // Is not reqired if examinationResultType is Failed
      (application.selectedPaymentType === PaymentType.OnlinePaymentForCompany || application.selectedPaymentType === PaymentType.ElectronicInvoice || application.selectedPaymentType === PaymentType.OnlineBulkPaymentForCompany),
    !!application.companyChosenForPayment && (!!application.companyChosenForPayment.companyRegistrationIdentifier || !!application.companyChosenForPayment.electronicInvoiceNumber),
    ValidationConstants.APPLICATION_COMPANYCHOSENFORPAYMENT,
    application.id,
    null,
    "Betaler skal vælges"
  );

  requiredIf(
    application.examinationResultType !== ExaminationResultType.Failed, // Is not reqired if examinationResultType is Failed
    !!application.citizen.birthPlaceCountry ? application.citizen.birthPlaceCountry.id : "",
    ValidationConstants.APPLICATION_CITIZEN_BIRTHPLACECOUNTRY,
    application.id
  );

  requiredIf(application.selectedPaymentType === PaymentType.ElectronicInvoice, !!application.companyChosenForPayment && application.companyChosenForPayment.reference, ValidationConstants.APPLICATION_COMPANYCHOSENFORPAYMENT_REFERENCE, application.id);

  requiredIfEnum(application.certificateProcessType === CertificateProcessType.Issuing && application.examinationResultType !== ExaminationResultType.Failed, application.criminalRecordDeclaration.status, ValidationConstants.APPLICATION_CRIMINALRECORDDECLARATION_STATUS, application.id);

  // If CriminalRecordDeclarationStatus is sat to Penalized, show error
  required(application.certificateProcessType === CertificateProcessType.Issuing && application.criminalRecordDeclaration.status !== CriminalRecordDeclarationStatus.Penalized, ValidationConstants.APPLICATION_CRIMINALRECORDDECLARATION_STATUS, application.id, null, "Du har sat kryds ved \"ja\" til strafbare forhold, hvorfor der ikke kan ansøges om et specialtransportassistenskort");

  if (application.examinationResultType !== ExaminationResultType.Failed) {
    dateGreaterThatToday(application.criminalRecordDeclaration.date, ValidationConstants.APPLICATION_CRIMINALRECORDDECLARATION_DATE, application.id);
  }

  requiredIf(application.certificateProcessType === CertificateProcessType.Issuing && application.examinationResultType !== ExaminationResultType.Failed, application.criminalRecordDeclaration.journalNumber, ValidationConstants.APPLICATION_CRIMINALRECORDDECLARATION_JOURNALNUMBER, application.id);

  requiredIf(application.certificateProcessType === CertificateProcessType.Issuing && application.examinationResultType !== ExaminationResultType.Failed, !!application.criminalRecordDeclaration.declarationOffice ? application.criminalRecordDeclaration.declarationOffice.id : "", ValidationConstants.APPLICATION_CRIMINALRECORDDECLARATION_DECLARATIONOFFICE, application.id);

  requiredIfDate(application.chosenEducationalCourseType == EducationalCourseType.ContinuingEducationForSta, application.previousCertificateExpireDate, ValidationConstants.APPLICATION_PREVIOUSCERTIFICATEEXPIREDATE, application.id);

  requiredEnum(application.chosenEducationalCourseType, ValidationConstants.APPLICATION_CHOSENEDUCATIONALCOURSETYPE, application.id, "Uddannelse skal vælges");

  requiredIf(application.examinationResultType !== ExaminationResultType.Failed, application.driverLicense.hasValidLicense, ValidationConstants.APPLICATION_DRIVERLICENSE_HASVALIDLICENSE, application.id);
};

const applicationForVubValidator = (application: IApplicationForVubVm) => {
  // If examinationResultType is sat to Failed, the educational worker should still be able to
  // submit the application, without filling out all the information that normally is required for a VUB application.
  requiredIf(
    application.examinationResultType !== ExaminationResultType.Failed, // Is not reqired if examinationResultType is Failed
    application.driverLicense.number,
    ValidationConstants.APPLICATION_DRIVERLICENSE_NUMBER,
    application.id
  );

  requiredIf(
    application.examinationResultType !== ExaminationResultType.Failed, // Is not reqired if examinationResultType is Failed
    application.base64ProfileImage,
    ValidationConstants.APPLICATION_BASE64PROFILEIMAGE,
    application.id
  );

  requiredIf(
    application.examinationResultType !== ExaminationResultType.Failed, // Is not reqired if examinationResultType is Failed
    application.base64SignatureImage,
    ValidationConstants.APPLICATION_BASE64SIGNATUREIMAGE,
    application.id,
    null,
    "Underskrift mangler"
  );

  requiredIfCharLength(!application.driverLicense.isLicenseForeign, application.driverLicense.number, 8, ValidationConstants.APPLICATION_DRIVERLICENSE_NUMBER, application.id, "Kørekortnummer skal indeholde minimum 8 tegn");

  requiredEnum(application.chosenEducationalCourseType, ValidationConstants.APPLICATION_CHOSENEDUCATIONALCOURSETYPE, application.id, "Uddannelse skal vælges");

  requiredEnum(application.examinationResultType, ValidationConstants.APPLICATION_EXAMINATIONRESULTTYPE, application.id);

  dateGreaterThatToday(application.examinationResultDate, ValidationConstants.APPLICATION_EXAMINATIONRESULTDATE, application.id);

  requiredIfEnum(
    application.examinationResultType !== ExaminationResultType.Failed, // Is not reqired if examinationResultType is Failed
    application.selectedPaymentType,
    ValidationConstants.APPLICATION_SELECTEDPAYMENTTYPE,
    application.id,
    "Betalingsform skal vælges"
  );

  requiredIf(
    application.examinationResultType !== ExaminationResultType.Failed && // Is not reqired if examinationResultType is Failed
      (application.selectedPaymentType === PaymentType.OnlinePaymentForCompany || application.selectedPaymentType === PaymentType.ElectronicInvoice || application.selectedPaymentType === PaymentType.OnlineBulkPaymentForCompany),
    !!application.companyChosenForPayment && (!!application.companyChosenForPayment.companyRegistrationIdentifier || !!application.companyChosenForPayment.electronicInvoiceNumber),
    ValidationConstants.APPLICATION_COMPANYCHOSENFORPAYMENT,
    application.id,
    null,
    "Betaler skal vælges"
  );

  requiredIf(
    application.examinationResultType !== ExaminationResultType.Failed, // Is not reqired if examinationResultType is Failed
    !!application.citizen.birthPlaceCountry ? application.citizen.birthPlaceCountry.id : "",
    ValidationConstants.APPLICATION_CITIZEN_BIRTHPLACECOUNTRY,
    application.id
  );

  requiredIf(application.selectedPaymentType === PaymentType.ElectronicInvoice, !!application.companyChosenForPayment && application.companyChosenForPayment.reference, ValidationConstants.APPLICATION_COMPANYCHOSENFORPAYMENT_REFERENCE, application.id);
};

const applicationForAdrValidator = (application: IApplicationForAdrVm) => {
  // If examinationResultType is sat to Failed, the educational worker should still be able to
  // submit the application, without filling out all the information that normally is required for a VUB application.
  requiredIf(
    application.examinationResultType !== ExaminationResultType.Failed, // Is not reqired if examinationResultType is Failed
    application.driverLicense.number,
    ValidationConstants.APPLICATION_DRIVERLICENSE_NUMBER,
    application.id
  );

  requiredIf(
    application.examinationResultType !== ExaminationResultType.Failed, // Is not reqired if examinationResultType is Failed
    application.base64ProfileImage,
    ValidationConstants.APPLICATION_BASE64PROFILEIMAGE,
    application.id
  );

  requiredIf(
    application.examinationResultType !== ExaminationResultType.Failed, // Is not reqired if examinationResultType is Failed
    application.base64SignatureImage,
    ValidationConstants.APPLICATION_BASE64SIGNATUREIMAGE,
    application.id,
    null,
    "Underskrift mangler"
  );

  requiredIfCharLength(!application.driverLicense.isLicenseForeign, application.driverLicense.number, 8, ValidationConstants.APPLICATION_DRIVERLICENSE_NUMBER, application.id, "Kørekortnummer skal indeholde minimum 8 tegn");

  requiredIfEnum(application.examinationResultType !== ExaminationResultType.Failed, application.driverLicense.chosenLicenseCategories, ValidationConstants.APPLICATION_DRIVERLICENSE_CHOSENLICENSECATEGORIES, application.id, "Kategorier skal vælges");

  requiredEnum(application.chosenEducationalCourseType, ValidationConstants.APPLICATION_CHOSENEDUCATIONALCOURSETYPE, application.id, "Uddannelse skal vælges");

  requiredEnum(application.examinationResultType, ValidationConstants.APPLICATION_EXAMINATIONRESULTTYPE, application.id);

  dateGreaterThatToday(application.examinationResultDate, ValidationConstants.APPLICATION_EXAMINATIONRESULTDATE, application.id);

  requiredIfEnum(
    application.examinationResultType !== ExaminationResultType.Failed, // Is not reqired if examinationResultType is Failed
    application.selectedPaymentType,
    ValidationConstants.APPLICATION_SELECTEDPAYMENTTYPE,
    application.id,
    "Betalingsform skal vælges"
  );

  requiredIf(
    application.examinationResultType !== ExaminationResultType.Failed && // Is not reqired if examinationResultType is Failed
      (application.selectedPaymentType === PaymentType.OnlinePaymentForCompany || application.selectedPaymentType === PaymentType.ElectronicInvoice || application.selectedPaymentType === PaymentType.OnlineBulkPaymentForCompany),
    !!application.companyChosenForPayment && (!!application.companyChosenForPayment.companyRegistrationIdentifier || !!application.companyChosenForPayment.electronicInvoiceNumber),
    ValidationConstants.APPLICATION_COMPANYCHOSENFORPAYMENT,
    application.id,
    null,
    "Betaler skal vælges"
  );

  requiredIf(
    application.examinationResultType !== ExaminationResultType.Failed, // Is not reqired if examinationResultType is Failed
    !!application.citizen.nationality ? application.citizen.nationality.id : "",
    ValidationConstants.APPLICATION_CITIZEN_NATIONALITY,
    application.id
  );

  requiredIf(application.selectedPaymentType === PaymentType.ElectronicInvoice, !!application.companyChosenForPayment && application.companyChosenForPayment.reference, ValidationConstants.APPLICATION_COMPANYCHOSENFORPAYMENT_REFERENCE, application.id);

  ifMaxCharLength(true, application.citizen.firstName, 32, ValidationConstants.APPLICATION_CITIZEN_USECUSTOMNAMEONCERTIFICATE, application.id);

  ifMaxCharLength(true, application.citizen.lastName, 24, ValidationConstants.APPLICATION_CITIZEN_USECUSTOMNAMEONCERTIFICATE, application.id);

  ifMaxCharLength(application.citizen.useCustomNameOnCertificate, application.citizen.customFirstName, 32, ValidationConstants.APPLICATION_CITIZEN_OVERWRITTENFIRSTNAME, application.id, "Fornavnet må højst indeholde 32 tegn");

  ifMaxCharLength(application.citizen.useCustomNameOnCertificate, application.citizen.customLastName, 24, ValidationConstants.APPLICATION_CITIZEN_OVERWRITTENLASTNAME, application.id, "Efternavnet må højst indeholde 24 tegn");

  requiredIf(application.citizen.useCustomNameOnCertificate, application.citizen.customFirstName, ValidationConstants.APPLICATION_CITIZEN_OVERWRITTENFIRSTNAME, application.id);

  requiredIf(application.citizen.useCustomNameOnCertificate, application.citizen.customLastName, ValidationConstants.APPLICATION_CITIZEN_OVERWRITTENLASTNAME, application.id);

  requiredIf(application.hasPreviousCertificateExpireDate, application.previousCertificateExpireDate, ValidationConstants.APPLICATION_PREVIOUSCERTIFICATEEXPIREDATE, application.id, null);

  requiredEnum(application.attendingSpecialCourse, ValidationConstants.APPLICATION_ATTENDINGSPECIALCOURSE, application.id);
};

const invoiceValidator = (invoice: IInvoiceVm, selectedPayor: SelectedPayorType) => {
  required(invoice.areTermsAndConditionsAccepted, "areTermsAndConditionsAccepted", invoice.id, false, "TEXT_CONFIRM_INFORMATION");

  // Address validation if "Anden betaler - Virksomhed" is selected as payor
  if (selectedPayor === SelectedPayorType.CompanyPaying && !!invoice.companyBilled) {
    requiredIf(selectedPayor === SelectedPayorType.CompanyPaying, invoice.companyBilled.name, "name", invoice.id);
  }
};

const applicationForExemptedValidator = (applicationForExempted: ICreateApplicationForExemptedCitizenVm) => {
  required(applicationForExempted.certificateProcessType === CertificateProcessType.NotSet ? "" : applicationForExempted.certificateProcessType, "ExempetedCertificateProcessTypeError", applicationForExempted.applicationId);
  required(applicationForExempted.actorSubmittingApplicationType === ActorSubmittingApplicationType.NotSet ? "" : applicationForExempted.actorSubmittingApplicationType, "ExempetedApplicationCitizenSubmitTypeError", applicationForExempted.applicationId);

  requiredIf(
    applicationForExempted.certificateProcessType === CertificateProcessType.RenewalBasedOnExternal || applicationForExempted.certificateProcessType === CertificateProcessType.Renewal || applicationForExempted.certificateProcessType === CertificateProcessType.ReplacementBasedOnExternal || applicationForExempted.certificateProcessType === CertificateProcessType.ChangeBasedOnExternal,
    applicationForExempted.driverlicenseNumber,
    "ExempetedDriversLicenseNumber",
    applicationForExempted.applicationId
  );

  requiredIf(applicationForExempted.certificateProcessType === CertificateProcessType.RenewalBasedOnExternal, applicationForExempted.formerLicenseType === FormerLicenseType.NotSet ? "" : applicationForExempted.formerLicenseType, "ExempetedFormerLicenseType", applicationForExempted.applicationId);

  requiredIf((applicationForExempted.certificateProcessType === CertificateProcessType.RenewalBasedOnExternal || applicationForExempted.certificateProcessType === CertificateProcessType.ChangeBasedOnExternal) && applicationForExempted.formerLicenseType === FormerLicenseType.TaxLicense, applicationForExempted.formerTaxLicenseNumber, "ExempetedFormerTaxLicenseNumber", applicationForExempted.applicationId);

  requiredIf(
    (applicationForExempted.certificateProcessType === CertificateProcessType.RenewalBasedOnExternal && applicationForExempted.formerLicenseType === FormerLicenseType.TaxLicense) || applicationForExempted.certificateProcessType === CertificateProcessType.ChangeBasedOnExternal || applicationForExempted.certificateProcessType === CertificateProcessType.ReplacementBasedOnExternal,
    applicationForExempted.formerIssuingMunicipality !== null ? applicationForExempted.formerIssuingMunicipality.municipalityName : "",
    "ExempetedFormerIssuingMunicipality",
    applicationForExempted.applicationId
  );

  requiredIfDate(
    (applicationForExempted.certificateProcessType === CertificateProcessType.RenewalBasedOnExternal && applicationForExempted.formerLicenseType === FormerLicenseType.TaxLicense) || applicationForExempted.certificateProcessType === CertificateProcessType.ChangeBasedOnExternal || applicationForExempted.certificateProcessType === CertificateProcessType.ReplacementBasedOnExternal,
    applicationForExempted.formerTaxLicenseExpireDate,
    "ExempetedFormerTaxLicenseExpireDate",
    applicationForExempted.applicationId
  );

  requiredIf(applicationForExempted.useSecondaryDeliveryAddress, applicationForExempted.secondaryDeliveryAddress.combinedStreetAddressLine, "ExempetedCombinedStreetAddressLine", applicationForExempted.applicationId);
  requiredIf(applicationForExempted.useSecondaryDeliveryAddress, applicationForExempted.secondaryDeliveryAddress.postalCode, "ExempetedPostalCode", applicationForExempted.applicationId);
  requiredIf(applicationForExempted.useSecondaryDeliveryAddress, applicationForExempted.secondaryDeliveryAddress.postalDistrict, "ExempetedPostalDistrict", applicationForExempted.applicationId);
  requiredIf(applicationForExempted.useSecondaryDeliveryAddress, applicationForExempted.secondaryDeliveryAddress.countryName, "ExempetedCountryName", applicationForExempted.applicationId);
  requiredIf(applicationForExempted.certificateType === CertificateType.Vub, !!applicationForExempted.educationalInstitution ? applicationForExempted.educationalInstitution.id : "", "ExempetedEducationalInstitution", applicationForExempted.applicationId);
  requiredIf(applicationForExempted.certificateType === CertificateType.Cub, !!applicationForExempted.educationalInstitution ? applicationForExempted.educationalInstitution.id : "", "ExempetedEducationalInstitution", applicationForExempted.applicationId);
  requiredIf(applicationForExempted.certificateType === CertificateType.Sta, !!applicationForExempted.educationalInstitution ? applicationForExempted.educationalInstitution.id : "", "ExempetedEducationalInstitution", applicationForExempted.applicationId);
  requiredIf(applicationForExempted.certificateType === CertificateType.Adr, !!applicationForExempted.educationalInstitution ? applicationForExempted.educationalInstitution.id : "", "ExempetedEducationalInstitution", applicationForExempted.applicationId);
};

const teamValidator = (team: ITeamVm) => {
  required(team.name, "teamNameError", team.id, true);
};

const applicationForReorderingOfCertificateValidator = (applicationForReorderingOfCertificate: ICreateApplicationForReorderingOfCertificateVm) => {
  required(applicationForReorderingOfCertificate.modifiedAddress.combinedStreetAddressLine, ValidationConstants.REORDER_APPLICATION_MODIFIEDADDRESS_COMBINEDSTREETADDRESSLINE, applicationForReorderingOfCertificate.applicationId);
  required(applicationForReorderingOfCertificate.modifiedAddress.postalCode, ValidationConstants.REORDER_APPLICATION_MODIFIEDADDRESS_POSTALCODE, applicationForReorderingOfCertificate.applicationId);
  required(applicationForReorderingOfCertificate.modifiedAddress.postalDistrict, ValidationConstants.REORDER_APPLICATION_MODIFIEDADDRESS_POSTALDISTRICT, applicationForReorderingOfCertificate.applicationId);
  required(applicationForReorderingOfCertificate.modifiedAddress.countryName, ValidationConstants.REORDER_APPLICATION_MODIFIEDADDRESS_COUNTRYNAME, applicationForReorderingOfCertificate.applicationId);

  requiredEnum(applicationForReorderingOfCertificate.selectedPaymentType, ValidationConstants.REORDER_APPLICATION_SELECTEDPAYMENTTYPE, applicationForReorderingOfCertificate.applicationId, "Betalingsform skal vælges");

  requiredIf(
    applicationForReorderingOfCertificate.selectedPaymentType === PaymentType.OnlinePaymentForCompany || applicationForReorderingOfCertificate.selectedPaymentType === PaymentType.ElectronicInvoice,
    !!applicationForReorderingOfCertificate.companyChosenForPayment && (!!applicationForReorderingOfCertificate.companyChosenForPayment.companyRegistrationIdentifier || !!applicationForReorderingOfCertificate.companyChosenForPayment.electronicInvoiceNumber),
    ValidationConstants.REORDER_APPLICATION_COMPANYCHOSENFORPAYMENT,
    applicationForReorderingOfCertificate.applicationId,
    null,
    "Betaler skal vælges"
  );

  requiredIf(applicationForReorderingOfCertificate.selectedPaymentType === PaymentType.ElectronicInvoice, !!applicationForReorderingOfCertificate.companyChosenForPayment && applicationForReorderingOfCertificate.companyChosenForPayment.reference, ValidationConstants.REORDER_APPLICATION_COMPANYCHOSENFORPAYMENT_REFERENCE, applicationForReorderingOfCertificate.applicationId);
};

const required = (value: boolean | string | number, property: string, id: string, checkFor0String?: boolean, customMessage?: string) => {
  const message = i18n.t(customMessage) || i18n.t("UI_VALIDATION_ERROR_REQUIRED");
  if (value && value.toString().trim() === "") {
    errors.push({ message, property, id, title: "" });
  } else if (!value || (checkFor0String && (value == "0" || value == "00000000-0000-0000-0000-000000000000"))) {
    errors.push({ message, property, id, title: "" });
  }
};
const requiredIf = (condition: boolean, value: boolean | string | number, property: string, id: string, checkFor0String?: boolean, customMessage?: string) => {
  if (condition) {
    required(value, property, id, checkFor0String, customMessage);
  }
};
const requiredIfDate = (condition: boolean, value: string, property: string, id: string) => {
  if (condition) {
    const date = moment(value, "DD-MM-YYYY");
    if (!(value != null && value.indexOf(" ") == -1 && date.isValid())) {
      const message = i18n.t("UI_VALIDATION_ERROR_REQUIRED");
      errors.push({ message, property, id, title: "" });
    }
  }
};
const requiredIfEnum = (condition: boolean, value: number, property: string, id: string, customMessage?: string) => {
  if (condition) {
    requiredEnum(value, property, id, customMessage);
  }
};
const requiredEnum = (value: number, property: string, id: string, customMessage?: string) => {
  const message = i18n.t(customMessage) || i18n.t("UI_VALIDATION_ERROR_REQUIRED");
  if (value == 0 || value == undefined) {
    errors.push({ message, property, id, title: "" });
  }
};
const dateGreaterThatToday = (value: string, property: string, id: string) => {
  const date = moment(value, "DD-MM-YYYY");
  if (value != null && value.indexOf(" ") == -1 && date.isValid()) {
    const isDateGreaterThanToday: boolean = date.toDate() > new Date();
    const message = i18n.t("UI_VALIDATION_ERROR_DATE_GREATER_THAN_TODAY");
    if (isDateGreaterThanToday) {
      errors.push({ message, property, id, title: "" });
    }
  } else {
    const message = i18n.t("UI_VALIDATION_ERROR_REQUIRED");
    errors.push({ message, property, id, title: "" });
  }
};
const requiredIfCharLength = (condition: boolean, value: string | number, charLength: number, property: string, id: string, customMessage?: string) => {
  const message = i18n.t(customMessage) || i18n.t("UI_VALIDATION_ERROR_REQUIRED");
  if (condition && value && value.toString().trim() !== "") {
    if (value.toString().trim().length !== charLength) {
      errors.push({ message, property, id, title: "" });
    }
  }
};

const ifMaxCharLength = (condition: boolean, value: string | number, charLength: number, property: string, id: string, customMessage?: string) => {
  const message = i18n.t(customMessage) || i18n.t("UI_VALIDATION_ERROR_REQUIRED");
  if (condition && value && value.toString().trim() !== "") {
    if (value.toString().length > charLength) {
      errors.push({ message, property, id, title: "" });
    }
  }
};
